import AppContext from 'App/AppContext';
import { IPCheckbox, IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import UserTasksChart, { IUserChart } from 'components/Charts/UserTasksChart';
import { useWidgetDrillDownNavigate } from 'components/Dashboard/WidgetDrillDownContext';
import PeriodFilter, { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import {
  getPeriodFilterEndDate,
  getPeriodFilterStartDate,
  getPeriodURLParams,
} from 'components/Filter/PeriodFilterHelper';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { UserTaskStat } from 'models/dashboardScene';
import Tag from 'models/tag';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataOpenTasksPerUser } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { fromApiDateOptional } from 'utils/datetime';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetOpenTasksPerUserConfig {
  tagIds: number[];

  showTags: boolean;

  tags: Tag[];

  overDeadline: boolean;

  period?: IPeriodFilterData;

  constructor() {
    this.tagIds = [];
    this.showTags = false;
    this.tags = [];
    this.overDeadline = false;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.tagIds = newRawConfig.tagIds ?? [];
        this.showTags = newRawConfig.showTags ?? false;
        this.overDeadline = newRawConfig.overDeadline ?? false;
        this.period = newRawConfig.period;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetOpenTasksPerUserConfig {
    const newConfig = new WidgetOpenTasksPerUserConfig();
    newConfig.tagIds = [...this.tagIds];
    newConfig.tags = [...this.tags];
    newConfig.showTags = this.showTags;
    newConfig.overDeadline = this.overDeadline;
    newConfig.period = this.period;

    return newConfig;
  }
}

interface IWidgetOpenTasksPerUserProps extends IWidgetRendererProps {}

const WidgetOpenTasksPerUser: FunctionComponent<IWidgetOpenTasksPerUserProps> = (
  props: IWidgetOpenTasksPerUserProps,
) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'widgets']);
  const navigate = useWidgetDrillDownNavigate();
  const projectId = props.projectId;
  const [data, setData] = useState<UserTaskStat[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<WidgetOpenTasksPerUserConfig | undefined>(undefined);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();
      setConfig(config);

      //calculate start and end dates
      const customStart = fromApiDateOptional(config.period?.customStart);
      const customEnd = fromApiDateOptional(config.period?.customEnd);
      const start = getPeriodFilterStartDate(config.period?.option, config.period?.auditYear, customStart, appContext);
      const end = getPeriodFilterEndDate(config.period?.option, config.period?.auditYear, customEnd, appContext);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      const data = await apiGetWidgetDataOpenTasksPerUser(
        config.tags,
        config.overDeadline,
        start,
        end,
        accessToken,
        props.abortSignal,
        projectId,
      );

      setData(data.sort((a, b) => b.taskCount - a.taskCount));
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConfig = (): WidgetOpenTasksPerUserConfig => {
    const newConfig = new WidgetOpenTasksPerUserConfig();
    newConfig.load(props.widget.widgetConfig);
    newConfig.tags = appContext.globalDataCache.tags.getItemsForId(newConfig.tagIds).filter((t) => t.tagId !== 0);

    if (props.period) {
      newConfig.period = props.period; //override period with the one from props
    }

    return newConfig;
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  const getChartData = (data: UserTaskStat[]): IUserChart[] => {
    const chartData: IUserChart[] = [];

    for (let idx = 0; idx < data.length; idx++) {
      const d = data[idx];
      const userData: IUserChart = {
        user: appContext.globalDataCache.users.get(d.userId).name,
        userId: d.userId,
        taskCount: d.taskCount,
      };
      const existingUser = chartData.find((c) => c.user === userData.user);
      if (existingUser) {
        //there can be users with the same name which is confusing in the graph and leads to React duplicate keys
        //add last 10 chars of email
        userData.user = userData.user + ' (' + appContext.globalDataCache.users.get(d.userId).email.slice(-10) + ')';
      }
      chartData.push(userData);
    }

    return chartData;
  };

  const onUserChartClick = (data: IUserChart) => {
    const baseParam = '/tasks/alltasks';

    const config = loadConfig();
    const tagStr = config.tags.length > 0 ? `&tags=${config.tags.map((t) => t.tagId.toString()).join(',')}` : '';
    const deadlineStr = config.overDeadline ? '&deadline=deadlineTooLate' : '';

    let periodStr = '';
    if (config.period) {
      periodStr = '&';
      periodStr += getPeriodURLParams(config.period);
    }

    const url = `${baseParam}?user=${data.userId}&open=true${tagStr}${deadlineStr}${periodStr}`;
    navigate(url);
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (data.length === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        {/* The widget counts tasks per user: an empty result means no user has an assigned task,
            not that no tasks exist. */}
        <IPText>{t('widgets:OpenTasksPerUser.NoAssignedTasks')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      {config && config.tags.length > 0 && config.showTags && (
        <IPStack.Item styles={{ root: { maxHeight: 44, overflowY: 'hidden' } }}>
          <KeyValueTags tags={config.tags} compact tagMaxWidth={200} />
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <UserTasksChart
          height={config && config.tags.length > 0 && config.showTags ? 160 : 220}
          width={280}
          userTasksData={getChartData(data)}
          onClick={onUserChartClick}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetOpenTasksPerUser;
//
// Config
//

interface IWidgetConfigWidgetOpenTasksPerUserProps extends IWidgetConfigRendererProps {}

export const WidgetConfigOpenTasksPerUser: FunctionComponent<IWidgetConfigWidgetOpenTasksPerUserProps> = (
  props: IWidgetConfigWidgetOpenTasksPerUserProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [showTags, setShowTags] = useState<boolean>(false);
  const [overDeadline, setOverDeadline] = useState<boolean>(false);
  const [period, setPeriod] = useState<IPeriodFilterData | undefined>(undefined);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  React.useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetOpenTasksPerUserConfig => {
    let newConfig = new WidgetOpenTasksPerUserConfig();
    if (props.widget.widgetConfig) {
      newConfig = JSON.parse(props.widget.widgetConfig);
      newConfig.tags = appContext.globalDataCache.tags.getItemsForId(newConfig.tagIds).filter((t) => t.tagId !== 0);
    }

    if (props.period) {
      newConfig.period = props.period; //override period with the one from props
    }

    return newConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();

      setOverDeadline(config.overDeadline);

      if (config.tags) {
        setSelectedTags(config.tags);
      }

      setShowTags(config.showTags);
      setPeriod(config.period);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (
    tags: Tag[],
    showTags: boolean,
    overDeadline: boolean,
    period: IPeriodFilterData | undefined,
  ): WidgetOpenTasksPerUserConfig => {
    const config = new WidgetOpenTasksPerUserConfig();
    config.tagIds = tags.map((t) => t.tagId);
    config.showTags = showTags;
    config.overDeadline = overDeadline;
    config.period = period;

    return config;
  };

  const onAdd = (tag: Tag) => {
    const newTags = [...selectedTags, tag];
    setSelectedTags(newTags);
    const config = getConfig(newTags, showTags, overDeadline, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onRemove = (tag: Tag) => {
    const newTags = selectedTags.filter((t) => t.tagId !== tag.tagId);
    setSelectedTags(newTags);
    const config = getConfig(newTags, showTags, overDeadline, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetShowTags = (checked: boolean) => {
    setShowTags(checked);
    const config = getConfig(selectedTags, checked, overDeadline, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetOverDeadline = (checked: boolean) => {
    setOverDeadline(checked);
    const config = getConfig(selectedTags, showTags, checked, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetPeriod = (period: IPeriodFilterData | undefined) => {
    setPeriod(period);
    const config = getConfig(selectedTags, showTags, overDeadline, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <PeriodFilter
          showOption={true}
          showCurrentAuditYear={true}
          storageKey={undefined}
          onUpdatePeriodOption={onSetPeriod}
          period={period}
          showAll={true}
          disabled={props.widget.dashboard?.isContextType()}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={overDeadline}
          label={t('widgets:OpenTasksPerUser.Config.LabelOverDeadline')}
          onChange={(ev, checked) => {
            onSetOverDeadline(checked ?? false);
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPLabel>{t('widgets:OpenTasksPerUser.Config.Label')}</IPLabel>
        <KeyValueTagPicker
          itemLimit={5}
          selectedTags={selectedTags}
          onAdd={onAdd}
          onRemove={onRemove}
          isLoading={isLoading}
          allowCreate={false}
        ></KeyValueTagPicker>
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={showTags}
          label={t('widgets:OpenTasksPerUser.Config.LabelShowTags')}
          onChange={(ev, checked) => {
            onSetShowTags(checked ?? false);
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};
